import __ from 'lodash';
import { AllActivePartnerGraphData, GraphData, XAxisData } from './interface';
import { ActivePartnerGroupCountData } from '../../../../__generated__/types';
import { IntlShape } from 'react-intl';
import { getI18n } from '../../../../utils/Utils';

enum Month {
    Jan = 1,
    Feb,
    Mar,
    Apr,
    May,
    Jun,
    Jul,
    Aug,
    Sep,
    Oct,
    Nov,
    Dec,
}

export function getDashBoardFilters(filters) {
    let filtersql = " AND duplicate = '1'";

    // Sunburst fixed layout array
    // Ignore [0] first one is company code
    if (filters.length > 1) {
        if (filters[1].path.indexOf('E7') > -1) {
            filtersql = filtersql.concat(" AND company = 'E7'");
        } else {
            filtersql = filtersql.concat(" AND company in('A1','MU','B7')");
        }
    }

    if (filters.length > 2) {
        if (filters[2].path.indexOf('Outgoing') > -1) {
            filtersql = filtersql.concat(" AND (process like '%OUT%' OR process like '%SBI%' OR process like '%PA%')");
        } else {
            filtersql = filtersql.concat(" AND process like '%INC%'");
        }
    }

    if (filters.length > 3) {
        filtersql = filtersql.concat(" AND process = '").concat(filters[3].path.trim()).concat("'");
    }

    if (filters.length > 4) {
        filtersql = filtersql.concat(" AND m_interface LIKE '%").concat(filters[4].path.trim()).concat("%'");
    }

    if (filters.length > 5) {
        filtersql = filtersql.concat(" AND status = '").concat(filters[5].path).concat("' ");
    }

    return filtersql;
}

const COLORS = [
    '#003366',
    '#006699',
    '#4cabce',
    '#e5323e',
    '#4C083E',
    '#990019',
    '#4cab88',
    '#e53287',
    '#000637',
    '#7B7D7D',
    '#0B5345',
];

const  getArraysOfValues = (ministerialMap: Map<string, any>) => {
    return Array.from(ministerialMap.values());
}

const monthNames = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

const getGraphXAxis = (intl?: IntlShape) => {
    const xAxis: XAxisData[] = [];
    const locale = intl?.locale || 'en';
    const monthNameList = locale === 'de' ? monthNames.de : monthNames.en;

    // Fixed group rolling 12 month
    // get the current date minus 12 month, is the starting point.
    const fromDate = new Date();
    fromDate.setUTCMonth(fromDate.getUTCMonth() - 12);
    for (let i = 1; i <= 13; i++) {
        const monthIndex = fromDate.getUTCMonth();
        xAxis.push({
            index: i,
            year: String(fromDate.getUTCFullYear()),
            month: monthIndex + 1,
            label: monthNameList[monthIndex].concat('-').concat(String(fromDate.getUTCFullYear()).substring(0, 4)),
        });
        fromDate.setUTCMonth(fromDate.getUTCMonth() + 1);
    }
    return xAxis;
}


export function getAllActiveInvoiceGraphOptions(activePartnerData, theme: 'dark' | 'light' = 'dark', intl?: IntlShape) {
    const isDark = theme === 'dark';
    const axisLabelColor = isDark ? '#bac6da' : '#1a3154';
    const axisLineColor = isDark ? 'rgba(186, 198, 218, 0.4)' : 'rgba(26, 49, 84, 0.25)';
    const splitLineColor = isDark ? 'rgba(186, 198, 218, 0.1)' : 'rgba(26, 49, 84, 0.1)';
    const legendTextColor = isDark ? '#dce5f2' : '#1a3154';
    const tooltipBg = isDark ? 'rgba(12, 20, 34, 0.95)' : 'rgba(255, 255, 255, 0.97)';
    const tooltipBorder = isDark ? 'rgba(149, 165, 192, 0.18)' : 'rgba(26, 49, 84, 0.2)';
    const tooltipTextColor = isDark ? '#f4f8ff' : '#1a3154';

    const graphData: AllActivePartnerGraphData = {};
    const categoryXAxis: GraphData = {};
    const categoryYAxis: GraphData = {};
    const series: GraphData[] = [];
    const mapLineChartMap = new Map();

    const activeGroupName = __.uniq(activePartnerData.map((groupItem) => groupItem.group));
    const graphXAxis = getGraphXAxis(intl);

    categoryXAxis.type = 'category';
    categoryYAxis.type = 'value';

    for (let j = 0; j < activeGroupName.length; j++) {
        const monthlyTotal: number[] = [];
        graphXAxis.map((item) => {
            //line chart mapping
            const lineChartTotal = activePartnerData
                .filter((row) => row.month === item.label)
                .map((item) => item.total)
                .reduce((prev, next) => prev + next, 0);
            mapLineChartMap.set(item.label, lineChartTotal);

            monthlyTotal.push(
                activePartnerData
                    .filter((row) => row.month === item.label && row.group === activeGroupName[j])
                    .map((item) => item.total)
                    .reduce((prev, next) => prev + next, 0)
            );
        });
        series.push({ name: `${activeGroupName[j]}`, type: 'bar', data: monthlyTotal });
    }
    categoryXAxis.data = Array.from(mapLineChartMap.keys());
    categoryXAxis.axisLabel = {
        color: axisLabelColor,
        interval: 0,
        hideOverlap: true,
        margin: 12,
        rotate: 28,
    };
    categoryXAxis.axisLine = {
        lineStyle: {
            color: axisLineColor,
        },
    };

    series.push({
        type: 'line',
        data: getArraysOfValues(mapLineChartMap),
        smooth: true,
        lineStyle: {
            color: '#f2c35b',
            width: 3,
        },
        itemStyle: {
            color: '#f2c35b',
        },
    });

    graphData.xAxis = categoryXAxis;
    graphData.yAxis = {
        ...categoryYAxis,
        axisLabel: {
            color: axisLabelColor,
        },
        splitLine: {
            lineStyle: {
                color: splitLineColor,
            },
        },
    };
    graphData.series = series;

    graphData.color = COLORS;
    graphData.legend = {
        data: activeGroupName,
        top: 8,
        left: 'center',
        orient: 'horizontal',
        itemGap: 14,
        textStyle: {
            color: legendTextColor,
        },
    };
    graphData.tooltip = {
        trigger: 'axis',
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        textStyle: {
            color: tooltipTextColor,
        },
        axisPointer: {
            type: 'shadow',
        },
    };
    graphData.grid = {
        left: 48,
        right: 24,
        top: 86,
        bottom: 68,
        containLabel: true,
    };
    return graphData;
}

export function getdashboardCardCountData(data) {
    return [
        { title: 'Total Invoices', icon: 'pie-chart', color: '#066d9a', value: data !== null ? data.total : 0 },
        { title: 'Passed Invoices', icon: 'check-square', color: '#05a233', value: data !== null ? data.pass : 0 },
        { title: 'Failed Invoice', icon: 'close-square', color: '#e9190b', value: data !== null ? data.fail : 0 },
        { title: 'In Transit Invoice', icon: 'car', color: '#e99813', value: data !== null ? data.transit : 0 },
        { title: 'I/B Invoices', icon: 'download', color: '#ffcb20', value: data !== null ? data.inbound : 0 },
        { title: 'O/B Invoices', icon: 'upload', color: '#7a8287', value: data !== null ? data.outbound : 0 },
    ];
}

enum Dashboard_Card_VALUE {
    TOTAL,
    INT,
    OUT,
    COMPLETED,
    OVERDUE,
    FAILED,
    INTRANSIT,
    BUSN,
}


export const getActivePartnerGroupBarChartOptions = (data: ActivePartnerGroupCountData[], theme: 'dark' | 'light' = 'dark', intl?: IntlShape) => {
    const isDark = theme === 'dark';
    const axisLabelColor = isDark ? '#bac6da' : '#1a3154';
    const axisLineColor = isDark ? 'rgba(186, 198, 218, 0.4)' : 'rgba(26, 49, 84, 0.25)';
    const splitLineColor = isDark ? 'rgba(186, 198, 218, 0.1)' : 'rgba(26, 49, 84, 0.1)';
    const titleColor = isDark ? '#f4f8ff' : '#1a3154';
    const tooltipBg = isDark ? 'rgba(12, 20, 34, 0.95)' : 'rgba(255, 255, 255, 0.97)';
    const tooltipBorder = isDark ? 'rgba(149, 165, 192, 0.18)' : 'rgba(26, 49, 84, 0.2)';
    const tooltipTextColor = isDark ? '#f4f8ff' : '#1a3154';
    const barLabelColor = isDark ? '#f4f8ff' : '#1a3154';

    return  {
        title: {
            text: intl ? getI18n('eim-active-partners-by-group', 'Active Partners by Group', intl) : 'Active Partners by Group',
            textStyle: {
                color: titleColor,
                fontWeight: 700,
            },
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            textStyle: {
                color: tooltipTextColor,
            },
        },
        grid: {
            left: 42,
            right: 18,
            top: 56,
            bottom: 72,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: data.map(item => item.group_name),
            axisLabel: {
                color: axisLabelColor,
                rotate: 50,
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor,
                },
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: axisLabelColor,
            },
            splitLine: {
                lineStyle: {
                    color: splitLineColor,
                },
            },
        },
        series: [
            {
                name: intl ? getI18n('eim-total-active-partners', 'Total Active Partners', intl) : 'Total Active Partners',
                type: 'bar',
                data: data.map(item => item.total_active_partner),
                itemStyle: {
                    color: '#57d5b1',
                },
                label: {
                    show: true,
                    position: 'top',
                    fontSize: 14,
                    rotate: 0,
                    color: barLabelColor,
                },
            }
        ],
    };
}

function getVolumeTrendDataZoomOptions(
    showZoomControls: boolean,
    axisLabelColor: string,
    zoomBorderColor: string,
    zoomFillerColor: string,
    zoomHandleColor: string,
) {
    if (!showZoomControls) {
        return [];
    }

    return [
        {
            type: 'inside',
            xAxisIndex: 0,
            filterMode: 'filter',
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: false,
        },
        {
            type: 'slider',
            xAxisIndex: 0,
            filterMode: 'filter',
            height: 22,
            bottom: 18,
            borderColor: zoomBorderColor,
            fillerColor: zoomFillerColor,
            handleStyle: {
                color: zoomHandleColor,
                borderColor: zoomHandleColor,
            },
            textStyle: {
                color: axisLabelColor,
            },
        },
    ];
}

const AVERAGE_TRANSIT_TREND_THEME_TOKENS = {
    dark: {
        axisLabelColor: '#bac6da',
        axisLineColor: 'rgba(186, 198, 218, 0.4)',
        splitLineColor: 'rgba(186, 198, 218, 0.1)',
        titleColor: '#f4f8ff',
        tooltipBg: 'rgba(12, 20, 34, 0.95)',
        tooltipBorder: 'rgba(149, 165, 192, 0.18)',
        tooltipTextColor: '#f4f8ff',
        meanLineColor: '#f2c35b',
        stdDevColor: '#9d9dff',
        zoomBorderColor: 'rgba(149, 165, 192, 0.22)',
        zoomFillerColor: 'rgba(87, 213, 177, 0.18)',
        zoomHandleColor: '#dce5f2',
    },
    light: {
        axisLabelColor: '#1a3154',
        axisLineColor: 'rgba(26, 49, 84, 0.25)',
        splitLineColor: 'rgba(26, 49, 84, 0.1)',
        titleColor: '#1a3154',
        tooltipBg: 'rgba(255, 255, 255, 0.97)',
        tooltipBorder: 'rgba(26, 49, 84, 0.2)',
        tooltipTextColor: '#1a3154',
        meanLineColor: '#e8a000',
        stdDevColor: '#6b7fd4',
        zoomBorderColor: 'rgba(26, 49, 84, 0.18)',
        zoomFillerColor: 'rgba(87, 213, 177, 0.22)',
        zoomHandleColor: '#1a3154',
    },
};

function getAverageTransitTrendThemeTokens(theme: 'dark' | 'light' = 'dark') {
    return AVERAGE_TRANSIT_TREND_THEME_TOKENS[theme];
}

function getAverageTransitTrendStats(
    trendData: Array<{ month: string; avgTransitDays: number }>,
    currentAvgTransitDays: number,
) {
    const validTrendData = trendData.filter((item) => Number.isFinite(item?.avgTransitDays));
    const meanTransitDays = validTrendData.length > 0
        ? validTrendData.reduce((sum, item) => sum + item.avgTransitDays, 0) / validTrendData.length
        : currentAvgTransitDays;
    const stdDev = validTrendData.length > 0
        ? Math.sqrt(validTrendData.reduce((sum, item) => sum + Math.pow(item.avgTransitDays - meanTransitDays, 2), 0) / validTrendData.length)
        : 0;

    return {
        meanTransitDays,
        stdDev,
    };
}

function createAverageTransitTrendTooltipFormatter(
    tooltipTextColor: string,
    lineColor: string,
    meanLineColor: string,
    stdDevColor: string,
    intl?: IntlShape,
) {
    return (params: any) => {
        if (!params || !Array.isArray(params) || params.length === 0) {
            return '';
        }

        const daysUnit = intl ? getI18n('eim-days-unit', 'days', intl) : 'days';
        let result = `<div style="color: ${tooltipTextColor};">${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
            const value = Number(param.value ?? param.data ?? 0);
            let bulletColor = lineColor;

            if (param.seriesName.includes('Mean')) {
                bulletColor = meanLineColor;
            } else if (param.seriesName.includes('±')) {
                bulletColor = stdDevColor;
            }

            result += `<span style="display:inline-block;margin-right:8px;width:10px;height:10px;border-radius:50%;background-color:${bulletColor};"></span>${param.seriesName}: ${value.toFixed(2)} ${daysUnit}<br/>`;
        });
        result += '</div>';
        return result;
    };
}

function getAverageTransitTrendLabels(intl?: IntlShape) {
    return {
        title: intl ? getI18n('eim-avg-transit-time-trend', 'Average Transit Time Trend', intl) : 'Average Transit Time Trend',
        days: intl ? getI18n('eim-days-unit', 'Days', intl) : 'Days',
        average: intl ? getI18n('eim-avg-transit-days', 'Avg Transit Days', intl) : 'Avg Transit Days',
        mean: intl ? getI18n('eim-mean-transit-days', 'Mean Transit Days', intl) : 'Mean Transit Days',
        upperStdDev: intl ? getI18n('eim-upper-std-dev', 'Upper ±1σ', intl) : 'Upper ±1σ',
        lowerStdDev: intl ? getI18n('eim-lower-std-dev', 'Lower ±1σ', intl) : 'Lower ±1σ',
    };
}

export const getAverageTransitDaysTrendChartOptions = (trendData: Array<{month: string, avgTransitDays: number}>, currentAvgTransitDays: number, theme: 'dark' | 'light' = 'dark', intl?: IntlShape) => {
    const labels = getAverageTransitTrendLabels(intl);
    const {
        axisLabelColor,
        axisLineColor,
        splitLineColor,
        titleColor,
        tooltipBg,
        tooltipBorder,
        tooltipTextColor,
        meanLineColor,
        stdDevColor,
        zoomBorderColor,
        zoomFillerColor,
        zoomHandleColor,
    } = getAverageTransitTrendThemeTokens(theme);
    const lineColor = '#57d5b1';
    const showZoomControls = trendData.length > 7;
    const { meanTransitDays, stdDev } = getAverageTransitTrendStats(trendData, currentAvgTransitDays);
    const upperBand = new Array(trendData.length).fill(meanTransitDays + stdDev);
    const lowerBand = new Array(trendData.length).fill(Math.max(0, meanTransitDays - stdDev));

    return {
        title: {
            text: labels.title,
            textStyle: {
                color: titleColor,
                fontWeight: 700,
            },
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            textStyle: {
                color: tooltipTextColor,
            },
            formatter: createAverageTransitTrendTooltipFormatter(tooltipTextColor, lineColor, meanLineColor, stdDevColor, intl),
        },
        grid: {
            left: 42,
            right: 18,
            top: 56,
            bottom: showZoomControls ? 78 : 42,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: trendData.map(item => item.month),
            axisLabel: {
                color: axisLabelColor,
                rotate: 45,
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor,
                },
            },
        },
        yAxis: {
            type: 'value',
            name: labels.days,
            axisLabel: {
                color: axisLabelColor,
            },
            splitLine: {
                lineStyle: {
                    color: splitLineColor,
                },
            },
        },
        dataZoom: getVolumeTrendDataZoomOptions(
            showZoomControls,
            axisLabelColor,
            zoomBorderColor,
            zoomFillerColor,
            zoomHandleColor,
        ),
        series: [
            {
                name: labels.average,
                type: 'line',
                data: trendData.map(item => item.avgTransitDays),
                smooth: true,
                lineStyle: {
                    color: lineColor,
                    width: 3,
                },
                itemStyle: {
                    color: lineColor,
                },
                symbolSize: 6,
                z: 3,
            },
            {
                name: labels.mean,
                type: 'line',
                data: new Array(trendData.length).fill(meanTransitDays),
                smooth: false,
                lineStyle: {
                    color: meanLineColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: meanLineColor,
                },
                symbolSize: 4,
                z: 2,
            },
            {
                name: labels.upperStdDev,
                type: 'line',
                data: upperBand,
                smooth: false,
                lineStyle: {
                    color: stdDevColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: stdDevColor,
                },
                symbolSize: 0,
                z: 1,
            },
            {
                name: labels.lowerStdDev,
                type: 'line',
                data: lowerBand,
                smooth: false,
                lineStyle: {
                    color: stdDevColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: stdDevColor,
                },
                symbolSize: 0,
                z: 1,
            }
        ],
    };
}

const getVolumeTrendStats = (trendData: Array<{ label: string; value: number }>) => {
    const sortedValues = trendData
        .map((item) => item.value)
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b);

    let median = 0;
    if (sortedValues.length > 0) {
        if (sortedValues.length % 2 === 0) {
            const rightIndex = sortedValues.length / 2;
            const leftIndex = rightIndex - 1;
            median = (sortedValues[leftIndex] + sortedValues[rightIndex]) / 2;
        } else {
            median = sortedValues[Math.floor(sortedValues.length / 2)];
        }
    }

    const mean = trendData.length > 0
        ? trendData.reduce((sum, item) => sum + item.value, 0) / trendData.length
        : 0;

    const variance = trendData.length > 0
        ? trendData.reduce((sum, item) => sum + Math.pow(item.value - mean, 2), 0) / trendData.length
        : 0;

    return {
        median,
        stdDev: Math.sqrt(variance),
    };
};

const createVolumeTrendTooltipFormatter = (
    tooltipTextColor: string,
    lineColor: string,
    medianLineColor: string,
    stdDevColor: string,
) => (params: any) => {
    if (!params || !Array.isArray(params) || params.length === 0) {
        return '';
    }

    let result = `<div style="color: ${tooltipTextColor};">${params[0].axisValue}<br/>`;
    params.forEach((param: any) => {
        const value = param.value ?? param.data;
        let bulletColor = lineColor;

        if (param.seriesName.includes('Median')) {
            bulletColor = medianLineColor;
        } else if (param.seriesName.includes('±')) {
            bulletColor = stdDevColor;
        }

        result += `<span style="display:inline-block;margin-right:8px;width:10px;height:10px;border-radius:50%;background-color:${bulletColor};"></span>${param.seriesName}: ${Number(value).toFixed(0)}<br/>`;
    });
    result += '</div>';
    return result;
};

const VOLUME_TREND_THEME_TOKENS = {
    dark: {
        axisLabelColor: '#bac6da',
        axisLineColor: 'rgba(186, 198, 218, 0.4)',
        splitLineColor: 'rgba(186, 198, 218, 0.1)',
        titleColor: '#f4f8ff',
        tooltipBg: 'rgba(12, 20, 34, 0.95)',
        tooltipBorder: 'rgba(149, 165, 192, 0.18)',
        tooltipTextColor: '#f4f8ff',
        medianLineColor: '#f2c35b',
        stdDevColor: '#9d9dff',
        zoomBorderColor: 'rgba(149, 165, 192, 0.22)',
        zoomFillerColor: 'rgba(87, 213, 177, 0.18)',
        zoomHandleColor: '#dce5f2',
    },
    light: {
        axisLabelColor: '#1a3154',
        axisLineColor: 'rgba(26, 49, 84, 0.25)',
        splitLineColor: 'rgba(26, 49, 84, 0.1)',
        titleColor: '#1a3154',
        tooltipBg: 'rgba(255, 255, 255, 0.97)',
        tooltipBorder: 'rgba(26, 49, 84, 0.2)',
        tooltipTextColor: '#1a3154',
        medianLineColor: '#e8a000',
        stdDevColor: '#6b7fd4',
        zoomBorderColor: 'rgba(26, 49, 84, 0.18)',
        zoomFillerColor: 'rgba(87, 213, 177, 0.22)',
        zoomHandleColor: '#1a3154',
    },
};

const getVolumeTrendThemeTokens = (theme: 'dark' | 'light' = 'dark') => {
    return VOLUME_TREND_THEME_TOKENS[theme];
};

export const getVolumeTrendWithMedianAndStdDevChartOptions = (
    trendData: Array<{ label: string; value: number }>,
    chartTitle: string,
    seriesLabel: string,
    theme: 'dark' | 'light' = 'dark',
    intl?: IntlShape,
) => {
    const lineColor = '#57d5b1';
    const {
        axisLabelColor,
        axisLineColor,
        splitLineColor,
        titleColor,
        tooltipBg,
        tooltipBorder,
        tooltipTextColor,
        medianLineColor,
        stdDevColor,
        zoomBorderColor,
        zoomFillerColor,
        zoomHandleColor,
    } = getVolumeTrendThemeTokens(theme);
    const showZoomControls = trendData.length > 7;
    const { median, stdDev } = getVolumeTrendStats(trendData);

    const upperBand = new Array(trendData.length).fill(median + stdDev);
    const lowerBand = new Array(trendData.length).fill(Math.max(0, median - stdDev));

    return {
        title: {
            text: chartTitle,
            textStyle: {
                color: titleColor,
                fontWeight: 700,
            },
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            textStyle: {
                color: tooltipTextColor,
            },
            formatter: createVolumeTrendTooltipFormatter(tooltipTextColor, lineColor, medianLineColor, stdDevColor),
        },
        grid: {
            left: 42,
            right: 18,
            top: 56,
            bottom: showZoomControls ? 78 : 42,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: trendData.map(item => item.label),
            axisLabel: {
                color: axisLabelColor,
                rotate: 45,
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor,
                },
            },
        },
        yAxis: {
            type: 'value',
            name: intl ? getI18n('eim-volume-unit', 'Volume', intl) : 'Volume',
            axisLabel: {
                color: axisLabelColor,
            },
            splitLine: {
                lineStyle: {
                    color: splitLineColor,
                },
            },
        },
        dataZoom: getVolumeTrendDataZoomOptions(showZoomControls, axisLabelColor, zoomBorderColor, zoomFillerColor, zoomHandleColor),
        series: [
            {
                name: seriesLabel,
                type: 'line',
                data: trendData.map(item => item.value),
                smooth: true,
                lineStyle: {
                    color: lineColor,
                    width: 3,
                },
                itemStyle: {
                    color: lineColor,
                },
                symbolSize: 6,
                z: 3,
            },
            {
                name: intl ? getI18n('eim-median-volume', 'Median Volume', intl) : 'Median Volume',
                type: 'line',
                data: new Array(trendData.length).fill(median),
                smooth: false,
                lineStyle: {
                    color: medianLineColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: medianLineColor,
                },
                symbolSize: 4,
                z: 2,
            },
            {
                name: intl ? getI18n('eim-upper-std-dev-volume', 'Upper ±1σ', intl) : 'Upper ±1σ',
                type: 'line',
                data: upperBand,
                smooth: false,
                lineStyle: {
                    color: stdDevColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: stdDevColor,
                },
                symbolSize: 0,
                z: 1,
            },
            {
                name: intl ? getI18n('eim-lower-std-dev-volume', 'Lower ±1σ', intl) : 'Lower ±1σ',
                type: 'line',
                data: lowerBand,
                smooth: false,
                lineStyle: {
                    color: stdDevColor,
                    width: 2,
                    type: 'dashed',
                },
                itemStyle: {
                    color: stdDevColor,
                },
                symbolSize: 0,
                z: 1,
            }
        ],
    };
};


